import moment from "moment"
import React, { useEffect } from "react"
import { Col, Dropdown, Row } from "react-bootstrap"
import { ChevronDown, ChevronUp } from "react-bootstrap-icons"
import BootstrapTable, { ColumnDescription } from "react-bootstrap-table-next"
import filterFactory, { Comparator, dateFilter, textFilter } from "react-bootstrap-table2-filter"
import paginationFactory from "react-bootstrap-table2-paginator"
import ToolkitProvider, { CSVExport } from "react-bootstrap-table2-toolkit"
import { useTranslation } from "react-i18next"
import swal from "sweetalert"
import { useAppDispatch } from "../../app/hooks"
import { SearchBar, SelectFilterColums } from "../../components/Tables/Columns"
import CustomTotal from "../../components/Tables/CustomTotal"
import pageListRenderer from "../../components/Tables/PageListRenderer"
import sizePerPageOptionRenderer from "../../components/Tables/SizePerPageOptionRenderer"
import { deletePromoCodes } from "../../store/promoCode.store"

let promoTypeFilter: any, userCountryFilter: any, startDateFilter: any, endDateFilter: any

function PromoCodeTable({
    promoCodes,
    PromoCodesShow,
    handleEditShow,
    handleClose,
    startDate,
    endDate
}: {
    promoCodes: any
    PromoCodesShow: Function
    handleEditShow: Function
    handleClose: Function
    startDate: string
    endDate: string
}) {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const onFilter = (value: string | number | null, id: string) => {
        if (!value) value = ""
        if (id === "coupon_type") promoTypeFilter(value)
        else if (id === "country_code") userCountryFilter(value)
    }

    useEffect(() => {
        startDateFilter({
            date: new Date(startDate + 3600 * 1000 * 24),
            comparator: Comparator.GE
        })
        endDateFilter({ date: new Date(endDate), comparator: Comparator.LE })
    }, [startDate, endDate])

    const onPromoCodeView = (data: Record<string, any>) => {
        PromoCodesShow(data)
    }

    const onPromoCodeEdit = (data: Record<string, any>) => {
        handleEditShow(data)
    }

    const onPromoCodeDelete = async (data: Record<string, any>) => {
        let promoDetail = { promocode_id: data.id }
        let deleteCode = await dispatch(deletePromoCodes(promoDetail))
        if (deleteCode?.payload?.data && deleteCode?.payload?.data?.status === true) {
            swal({
                title: "",
                text: t("Promo_Code_is_deleted"),
                icon: "success"
            }).then(function () {
                handleClose()
            })
        } else {
            swal({
                title: "",
                text: deleteCode?.payload?.data?.message,
                icon: "error"
            }).then(function () {
                handleClose()
            })
        }
    }

    const { ExportCSVButton } = CSVExport
    const columns = [
        {
            dataField: "id",
            text: t("NO")
        },
        {
            text: t("Promo_Name"),
            dataField: "name",
            formatter: (cell: string) => {
                return <div style={{ color: "#1E88E5" }}>{cell}</div>
            }
        },
        {
            text: t("Type"),
            dataField: "coupon_type",
            formatter: (cell: string) => {
                return cell?.charAt(0)?.toUpperCase() + cell.slice(1)
            },
            filter: textFilter({
                getFilter: (filter) => {
                    promoTypeFilter = filter
                }
            }),
            csvFormatter: (cell: string, row: Record<string, any>) => {
                return cell?.charAt(0)?.toUpperCase() + cell.slice(1)
            }
        },
        {
            text: t("Promo_Code"),
            dataField: "code"
        },
        {
            text: t("Value"),
            dataField: "discount",
            formatter: (cell: string | number, row: Record<string, any>) => {
                return row?.discount_type === "flat" ? (
                    <div>
                        <b>{cell}</b>
                        {t("$")}
                    </div>
                ) : (
                    <div>
                        <b>{cell}</b>
                        {t("%")}
                    </div>
                )
            },
            csvFormatter: (cell: string | number, row: Record<string, any>) => {
                return row?.discount_type === "flat" ? cell + t("$") : cell + t("%")
            }
        },
        {
            text: t("Discount"),
            dataField: "discount_type",
            formatter: (cell: string) => {
                return cell === "percent" ? (
                    <span className="percent_discount">{t("Percentage")}</span>
                ) : (
                    <span className="flat_discount">{t("Flat_Discount")}</span>
                )
            },
            csvFormatter: (cell: string, row: Record<string, any>) => {
                return cell === "percent" ? t("Percentage") : t("Flat_Discount")
            }
        },
        {
            dataField: "start_date",
            text: t("Start_Date"),
            sort: true,
            filter: dateFilter({
                className: "custom-dateFilter-class",
                getFilter: (filter) => {
                    startDateFilter = filter
                }
            }),
            formatter: (cell: number) => {
                if (cell) return moment(cell).format("DD MMM yyyy")
            },
            csvFormatter: (cell: number, row: Record<string, any>) => {
                if (cell) return moment(cell).format("DD MMM yyyy")
            }
        },
        {
            dataField: "expiry_date",
            text: t("Expiry_Date"),
            sort: true,
            filter: dateFilter({
                className: "custom-dateFilter-class",
                getFilter: (filter) => {
                    endDateFilter = filter
                }
            }),
            formatter: (cell: number) => {
                if (cell) return moment(cell).format("DD MMM yyyy")
            },
            csvFormatter: (cell: number, row: Record<string, any>) => {
                if (cell) return moment(cell).format("DD MMM yyyy")
            }
        },

        {
            text: t("Country"),
            dataField: "country_code",
            filter: textFilter({
                getFilter: (filter) => {
                    userCountryFilter = filter
                }
            }),
            formatter: (cell: string, row: Record<string, any>) => {
                return <div className="w-25">{cell ? cell : "All"}</div>
            },
            csvFormatter: (cell: string, row: Record<string, any>) => {
                return cell ? cell : "All"
            }
        },
        {
            text: t("Status"),
            dataField: "active",
            sort: true,
            formatter: (cell: boolean) => {
                return cell ? (
                    <span className="active_status">{t("Active")}</span>
                ) : (
                    <span className="inactive_status">{t("Inactive")}</span>
                )
            }
        },
        {
            dataField: "",
            text: t("Actions"),
            csvExport: false,
            formatter: (cell: number, row: Record<string, any>) => {
                return (
                    <Dropdown className="actions-menu">
                        <Dropdown.Toggle variant="primary" id="dropdown-basic" size={"sm"} className="py-0">
                            {t("Actions")}
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                            {["Edit", "View", "Delete"].map((action) => (
                                <Dropdown.Item className="py-0">
                                    {action === "View" && (
                                        <div onClick={() => onPromoCodeView(row)}>{action}</div>
                                    )}
                                    {action === "Edit" && (
                                        <div onClick={() => onPromoCodeEdit(row)}>{action}</div>
                                    )}
                                    {action === "Delete" && (
                                        <div onClick={() => onPromoCodeDelete(row)}>{action}</div>
                                    )}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                )
            }
        },
        {
            text: t("Promo_Code"),
            dataField: "code",
            hidden: true
        },
        {
            text: t("User_Type"),
            dataField: "is_open",
            hidden: true,
            csvFormatter: (cell: number, row: Record<string, any>) => {
                return cell === 1 ? t("All") : t("Single_Multiple")
            }
        }
    ]

    const expandRow = {
        renderer: (row: Record<string, any>): JSX.Element => {
            return (
                <Row className="expanding-row px-4 py-4">
                    <Col xs={12} sm={12} md={12} lg={5}>
                        <Row className="expand-data">
                            <Col xs={12} sm={12} md={12} lg={5} className="label">
                                {t("Promo_Code")}
                            </Col>
                            <Col xs={12} sm={12} md={12} lg={7} className="value">
                                {row.code}
                            </Col>
                        </Row>
                        <Row className="expand-data">
                            <Col xs={12} sm={12} md={12} lg={5} className="label">
                                {t("Discount_Type")}
                            </Col>
                            <Col xs={12} sm={12} md={12} lg={7} className="value">
                                {row.discount_type === "percent" ? t("Percentage") : t("Flat_Discount")}
                            </Col>
                        </Row>
                        <Row className="expand-data">
                            <Col xs={12} sm={12} md={12} lg={5} className="label">
                                {t("Start_Date")}
                            </Col>
                            <Col xs={12} sm={12} md={12} lg={7} className="value">
                                {moment(row.start_date).format("DD MMM yyyy")}
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={7}>
                        <Row className="expand-data">
                            <Col xs={12} sm={12} md={12} lg={3} className="label">
                                {t("Country")}
                            </Col>
                            <Col xs={12} sm={12} md={12} lg={9} className="value">
                                {row.country_code ? row.country_code : "All"}
                            </Col>
                        </Row>
                        <Row className="expand-data">
                            <Col xs={12} sm={12} md={12} lg={3} className="label">
                                {t("User_Type")}
                            </Col>
                            <Col xs={12} sm={12} md={12} lg={9} className="value">
                                {row.is_open === 1 ? t("All") : t("Single_Multiple")}
                            </Col>
                        </Row>
                        <Row className="expand-data">
                            <Col xs={12} sm={12} md={12} lg={3} className="label">
                                {t("Expiry_Date")}
                            </Col>
                            <Col xs={12} sm={12} md={12} lg={9} className="value">
                                {moment(row.expiry_date).format("DD MMM yyyy")}
                            </Col>
                        </Row>
                    </Col>
                </Row>
            )
        },
        showExpandColumn: true,
        expandByColumnOnly: true,
        expandHeaderColumnRenderer: ({ isAnyExpands }: { isAnyExpands: boolean }) => {
            return <></>
        },
        expandColumnRenderer: ({ expanded }: { expanded: boolean }) => {
            if (expanded) {
                return <ChevronUp color="#2D3748" size={10} className="mx-2 my-3 arrow-icon" />
            }
            return <ChevronDown color="#2D3748" size={10} className="mx-2 my-3 arrow-icon" />
        }
    }

    const options = {
        sizePerPage: 50,
        hideSizePerPage: false,
        hidePageListOnlyOnePage: true,
        sizePerPageOptionRenderer,
        showTotal: true,
        pageListRenderer,
        paginationTotalRenderer: CustomTotal
    }
    return (
        <div className="table-responsive">
            {columns && (
                <ToolkitProvider
                    keyField="id"
                    data={promoCodes}
                    columns={columns as ColumnDescription<any, any>[]}
                    search={{ searchFormatted: true }}
                    exportCSV={{
                        fileName: "promoCodesTable.csv",
                        noAutoBOM: false,
                        exportAll: false,
                        onlyExportFiltered: true
                    }}>
                    {(props) => (
                        <div className="table-container my-4">
                            <Row className="search mx-3  suites-search">
                                <Col xs={12} sm={6} md={6} lg={2} className="pl-4">
                                    <SelectFilterColums
                                        preFilteredRows={props.baseProps.data}
                                        id="coupon_type"
                                        text={t("Promo_Type")}
                                        {...props.searchProps}
                                        onFilterClick={onFilter}
                                    />
                                </Col>
                                <Col xs={12} sm={6} md={6} lg={2}>
                                    <SelectFilterColums
                                        preFilteredRows={props.baseProps.data}
                                        id="country_code"
                                        text={t("Country")}
                                        {...props.searchProps}
                                        onFilterClick={onFilter}
                                    />
                                </Col>
                                <Col xs={12} sm={12} md={12} lg={8} className="px-2 search-col">
                                    <SearchBar {...props.searchProps} />
                                    <ExportCSVButton
                                        {...props.csvProps}
                                        className=" promoCodeExportButton d-none">
                                        Export
                                    </ExportCSVButton>
                                </Col>
                            </Row>
                            <Row className="table-data my-3">
                                <Col>
                                    <BootstrapTable
                                        {...props.baseProps}
                                        wrapperClasses="table-wrapper my-3 mx-4 "
                                        headerClasses="header-class"
                                        bordered={false}
                                        filter={filterFactory()}
                                        noDataIndication={t("No_Record")}
                                        rowClasses="custom-table-row"
                                        condensed={true}
                                        pagination={paginationFactory(options)}
                                        sort={{
                                            dataField: "created_at",
                                            order: "desc"
                                        }}
                                        expandRow={expandRow}
                                    />
                                </Col>
                            </Row>
                        </div>
                    )}
                </ToolkitProvider>
            )}
        </div>
    )
}

export default PromoCodeTable
